/**
 * The input driver a `defineSandboxTest` step's `press` / `click` / `type` goes through in a page, and the
 * focus-scoped key routing it relies on. The app installs it once at load (SandboxRunnerProvider.tsx); the parity
 * harness installs the same one, so the browser side of the parity suite drives input exactly as the app does.
 */
import { setInputDriver } from '@gotti/sandbox-core';
import * as TestUtils from '@gotti/test-utils';
import { focusTestClient } from './testInputFocus';

// Wire the declarative defineSandboxTest input layer.
//
// A `press` in a defineSandboxTest step must drive REAL keyboard input into
// whatever the running game's client InputSystem listens on. The recurring
// trap (called out in the defineSandboxTest design doc — "window vs document
// listeners") is the dispatch TARGET: `InputSimulator` dispatches its
// KeyboardEvents on `window`, but game templates conventionally register their
// `keydown`/`keyup` listeners on `document` (e.g. the player-movement template's
// `InputSystem.onStart` does `document.addEventListener('keydown', ...)`). An
// event dispatched on `window` does NOT reach `document` listeners (window is
// the ancestor; events don't propagate downward), so an InputSimulator press
// would silently miss the template entirely.
//
// Dispatching on `document` is the bulletproof target: the event fires
// `document` listeners directly AND bubbles UP to `window`, so BOTH listener
// conventions receive it. We also make `press` a sustained HOLD (keydown now,
// keyup scheduled ~700ms later) rather than InputSimulator's 100ms tap, so a
// continuous-movement template (which emits a PLAYER_INPUT packet every tick
// while the key is held) actually accumulates movement across a realtime step
// window before release. This makes `client: { press: 'ArrowRight' }` genuinely
// move the player — no hand-fed packets, no raw event plumbing in the spec.
// Derive a physical `code` from the logical `key` so a generated InputSystem
// that reads `event.code` (e.g. `e.code === 'ArrowUp'` / `'KeyW'` — a valid,
// common pattern the framework permits) receives input too. Without this the
// event's `code` is '' and any e.code-based handler silently sees nothing,
// while an e.key-based one works — a high-variance input failure that depends
// purely on which accessor the model happened to emit.
function keyToCode(key: string): string {
  if (/^Arrow(Up|Down|Left|Right)$/.test(key)) return key;
  if (/^[a-zA-Z]$/.test(key)) return `Key${key.toUpperCase()}`;
  if (/^[0-9]$/.test(key)) return `Digit${key}`;
  if (key === ' ' || key === 'Spacebar') return 'Space';
  if (key === 'Escape' || key === 'Enter' || key === 'Tab') return key;
  return '';
}

function dispatchKey(type: 'keydown' | 'keyup', key: string): void {
  document.dispatchEvent(
    new KeyboardEvent(type, { key, code: keyToCode(key), bubbles: true, cancelable: true }),
  );
}
const HOLD_MS = 700;

// Which booted client currently has input focus, for a multi-client test. This
// is the SAME seam the live split-screen UI uses: a keypress only drives the
// FOCUSED client, because each client's `InputSystem` gates on
// `$api.isFocused(this.$client.id)`. Each booted client's `isFocused` closure
// (wired in the booter below) returns `i === activeTestClientIndex`, so setting
// this before a dispatch routes that keypress to exactly one client. A step's
// `clientIndex` selects the target (default 0); single-client tests never move
// off 0, so behaviour is unchanged. Module-scoped because the input driver
// (installed once at module load) and the per-client closures (created per
// boot) must share it.
let activeTestClientIndex = 0;


// The canvas of the test's target client. Multi-client tests mount each client's
// canvas inside a wrapper tagged `data-test-viz-client="player-N"`
// (mountTestVisualization); a single-client run just has the live-preview
// canvas. A step's `click: {x, y}` is CANVAS-LOCAL, so the driver needs this
// element to map those coords to viewport space.
const testClientCanvas = (idx: number): HTMLCanvasElement | null => {
  if (typeof document === 'undefined') return null;
  const tagged = document.querySelector<HTMLCanvasElement>(
    `[data-test-viz-client="player-${idx + 1}"] canvas.sandbox-canvas`,
  );
  if (tagged) return tagged;
  const all = document.querySelectorAll<HTMLCanvasElement>('canvas.sandbox-canvas');
  return all[idx] ?? all[0] ?? null;
};

/** Installs the driver and the routing; idempotent in effect (each install replaces the driver). */
export function installTestInputDriver(): void {
  // Route window key events to only the focused client's listeners (each client's
  // listeners are scoped under its input token — see testInputFocus.ts). Generated
  // code calls `window.addEventListener('keydown', …)` with NO focus gate; the
  // framework delivers each press to exactly one client. Installed once here.
  TestUtils.installFocusScopedInput();

  setInputDriver({
    press: (k: string, clientIndex = 0) => {
      const idx = clientIndex ?? 0;
      activeTestClientIndex = idx;
      focusTestClient(idx);
      dispatchKey('keydown', k);
      // Release to the SAME client that pressed (capture idx), then restore the
      // previously-active client so a concurrently-held other client isn't
      // disrupted by this release. In non-realtime runs the key stays "held"
      // (this fires after the run) so continuous-movement templates accumulate.
      setTimeout(() => {
        const prev = activeTestClientIndex;
        activeTestClientIndex = idx;
        focusTestClient(idx);
        dispatchKey('keyup', k);
        activeTestClientIndex = prev;
        focusTestClient(prev);
      }, HOLD_MS);
    },
    click: (x: number, y: number, clientIndex = 0) => {
      const idx = clientIndex ?? 0;
      activeTestClientIndex = idx;
      focusTestClient(idx);
      // The step's (x, y) are CANVAS-LOCAL. Offset by the target canvas's bounding
      // rect so the dispatched event's clientX/clientY are VIEWPORT coords that
      // (a) hit-test onto THAT canvas (elementFromPoint), and (b) make the game's
      // `event.clientX - rect.left` recover exactly the local x/y the test asked
      // for (the rect offset cancels out — independent of any CSS canvas scaling).
      const canvas = testClientCanvas(idx);
      if (canvas) {
        const rect = canvas.getBoundingClientRect();
        TestUtils.InputSimulator.click(rect.left + x, rect.top + y);
      } else {
        TestUtils.InputSimulator.click(x, y);
      }
    },
    type: (t: string, clientIndex = 0) => {
      activeTestClientIndex = clientIndex ?? 0;
      focusTestClient(activeTestClientIndex);
      for (const ch of t) { dispatchKey('keydown', ch); dispatchKey('keyup', ch); }
    },
  });
}
